import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Plane, Camera, Share2, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useLang } from "./LangContext";

export default function Services() {
  const { t } = useLang();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  const services = [
    {
      icon: Plane,
      title: t.services.drone.title,
      desc: t.services.drone.desc,
      image: "https://customer-assets.emergentagent.com/job_prodimmo-media/artifacts/tpefhe4s_ChatGPT%20Image%2026%20fe%CC%81vr.%202026%2C%2013_23_50.png",
      badge: t.services.drone.badge,
      link: "/drone-fpv-immobilier",
    },
    {
      icon: Camera,
      title: t.services.video.title,
      desc: t.services.video.desc,
      image: "https://customer-assets.emergentagent.com/job_prodimmo-media/artifacts/6rx5mzjk_IMG_6755.jpg",
      badge: t.services.video.badge,
      link: "/video-immobilier",
    },
    {
      icon: Share2,
      title: t.services.social.title,
      desc: t.services.social.desc,
      image: "https://images.pexels.com/photos/15713296/pexels-photo-15713296.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
      badge: t.services.social.badge,
      link: null,
      cta: "Sur devis",
    },
  ];

  return (
    <section id="prestations" data-testid="services-section" className="py-20 md:py-32 bg-[#F8FAFC]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-[#73B5E2] font-medium text-sm uppercase tracking-widest">
            {t.services.label}
          </span>
          <h2
            data-testid="services-title"
            className="mt-3 text-3xl sm:text-4xl font-bold text-slate-900"
            style={{ fontFamily: 'Outfit' }}
          >
            {t.services.title}
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {services.map((service, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: i * 0.15 }}
              className="service-card bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-slate-100 group flex flex-col"
              data-testid={`service-card-${i}`}
            >
              <div className="relative h-56 overflow-hidden">
                <img
                  src={service.image}
                  alt={service.title}
                  className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-110 ${i === 1 ? "object-[center_70%]" : ""}`}
                />
                <div className="absolute top-4 left-4">
                  <span className="bg-white/90 backdrop-blur-sm text-[#73B5E2] text-xs font-semibold px-3 py-1 rounded-full">
                    {service.badge}
                  </span>
                </div>
              </div>
              <div className="p-6 flex flex-col flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <div className="service-icon w-10 h-10 rounded-xl bg-[#73B5E2]/10 flex items-center justify-center transition-all duration-300">
                    <service.icon className="w-5 h-5 text-[#73B5E2]" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit' }}>
                    {service.title}
                  </h3>
                </div>
                <p className="text-slate-600 text-sm leading-relaxed flex-1">{service.desc}</p>
                {service.link ? (
                  <Link
                    to={service.link}
                    data-testid={`service-link-${i}`}
                    className="mt-5 inline-flex items-center justify-center gap-2 w-full bg-[#73B5E2] text-white hover:bg-[#5AA0D0] rounded-full px-6 py-3 text-sm font-medium transition-all hover:scale-[1.02] shadow-md shadow-blue-500/15 group/btn"
                  >
                    En savoir plus
                    <ArrowRight className="w-4 h-4 transition-transform group-hover/btn:translate-x-1" />
                  </Link>
                ) : service.cta ? (
                  <a
                    href="#contact"
                    onClick={(e) => { e.preventDefault(); document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" }); }}
                    data-testid={`service-link-${i}`}
                    className="mt-5 inline-flex items-center justify-center gap-2 w-full border-2 border-[#73B5E2] text-[#73B5E2] hover:bg-[#73B5E2] hover:text-white rounded-full px-6 py-3 text-sm font-medium transition-all hover:scale-[1.02]"
                  >
                    {service.cta}
                    <ArrowRight className="w-4 h-4" />
                  </a>
                ) : null}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
